import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

const avengersColors = {
  red: '#a50000',
  blue: '#3268bd',
  green: '#00991f',
  gray: '#999999',
  purple: '#460061'
};

const heroTypes = [
  {
    id: 'sentinel',
    name: 'Sentinelle',
    description: "Architecte de l'IA souveraine. La puissance locale au service d'une éthique sans compromis.",
    icon: '🛡️',
    gradient: `linear-gradient(135deg, ${avengersColors.blue}, #4facfe)`,
    specs: ['Mac Studio Ultra', 'Neural RAID 0', 'Éthique Swiss']
  },
  {
    id: 'novaforge',
    name: 'NovaForge',
    description: "Là où l'IA de pointe devient impact. Forger l'avenir pour l'éducation et l'open source.",
    icon: '⚡',
    gradient: `linear-gradient(135deg, ${avengersColors.purple}, #f093fb)`,
    specs: ['LLM 670B Params', 'Tensor Clusters', 'Open Source']
  },
  {
    id: 'cybeth',
    name: 'CybETH Prowess',
    description: "Gardiens de l'intégrité numérique. Cybersécurité et hacking éthique pour une IA infaillible.",
    icon: '🔐',
    gradient: `linear-gradient(135deg, ${avengersColors.red}, #fa709a)`,
    specs: ['Quantum Security', 'Zero Trust', 'Blockchain']
  },
  {
    id: 'genius-core',
    name: 'Genius Core',
    description: "Innovation éducative révolutionnaire. L'IA au service de l'apprentissage adaptatif.",
    icon: '🧠',
    gradient: `linear-gradient(135deg, ${avengersColors.green}, #43e97b)`,
    specs: ['ML Adaptatif', 'EdTech IA', 'Genius Framework']
  },
  {
    id: 'cloud-nexus',
    name: 'Cloud Nexus',
    description: "Infrastructure souveraine suisse. Cloud local haute performance pour l'IA critique.",
    icon: '☁️',
    gradient: 'linear-gradient(135deg, #30cfd0, #330867)',
    specs: ['Swiss Cloud', 'Edge Computing', 'Souveraineté']
  },
  {
    id: 'data-weaver',
    name: 'Data Weaver',
    description: "Tisseur de données intelligentes. Big Data et analytics pour des insights révolutionnaires.",
    icon: '📊',
    gradient: 'linear-gradient(135deg, #ff9500, #fee140)',
    specs: ['Big Data', 'Analytics IA', 'Data Science']
  }
];

const HeroCard: React.FC<{ hero: typeof heroTypes[0]; index: number }> = ({ hero, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative rounded-lg overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300"
      style={{
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        background: 'rgba(128, 128, 128, 0.12)',
        backdropFilter: 'blur(10px) saturate(150%)',
        WebkitBackdropFilter: 'blur(10px) saturate(150%)',
        border: '1px solid rgba(200, 200, 200, 0.3)',
      }}
    >
      {/* Gradient Header avec Icône */}
      <div
        className="h-32 relative flex items-center justify-center"
        style={{ background: hero.gradient }}
      >
        <div className="text-6xl">
          {hero.icon}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl md:text-2xl font-bold text-black mb-2 drop-shadow-sm">
          {hero.name}
        </h3>
        <p className="text-black text-base leading-relaxed mb-4 font-medium drop-shadow-sm">
          {hero.description}
        </p>

        {/* Technical Specs */}
        <div className="flex flex-wrap gap-2">
          {hero.specs.map((spec, i) => (
            <span
              key={i}
              className="text-xs px-2 py-1 text-black rounded-full font-semibold"
              style={{
                background: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
              }}
            >
              {spec}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const ModernSwissAvengers: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message || 'Message envoyé avec succès !');
          setSubmitStatus('success');
          setFormData({ name: '', email: '', organization: '', message: '' });
        } else {
          toast.error(result.message || 'Une erreur est survenue côté serveur.');
          setSubmitStatus('error');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.message || `Erreur ${response.status}: ${response.statusText}`);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      toast.error('Impossible de contacter le serveur. Veuillez réessayer.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-6">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#333333]">
            Swiss Avengers
          </h2>
          <p className="text-xl text-[#666666] max-w-3xl mx-auto">
            L'équipe d'élite qui propulse votre transformation digitale vers de nouveaux sommets
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {heroTypes.map((hero, index) => (
            <HeroCard key={hero.id} hero={hero} index={index} />
          ))}
        </div>

        {/* Contact Form */}
        <div className="mt-16">
          <div className="max-w-2xl mx-auto">
            <motion.div
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
              style={{
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                borderRadius: '24px',
                overflow: 'hidden',
                background: 'rgba(128, 128, 128, 0.12)',
                backdropFilter: 'blur(10px) saturate(150%)',
                WebkitBackdropFilter: 'blur(10px) saturate(150%)',
                border: '1px solid rgba(200, 200, 200, 0.3)',
                position: 'relative'
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, #5e5ce6, #bf5af2)',
                  padding: '20px',
                  borderTopLeftRadius: '24px',
                  borderTopRightRadius: '24px'
                }}
              >
                <img
                  src="/images/genius-ai-icon.png"
                  alt="Contact Form"
                  className="w-20 h-20 mx-auto my-3 drop-shadow-lg"
                  style={{ display: 'block' }}
                />
              </div>

              <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#444444' }}>
                      Nom
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#f9f9f9',
                        fontSize: '15px',
                        color: '#333333',
                        transition: 'border-color 0.2s ease',
                      }}
                      className="focus:outline-none focus:border-blue-400"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#444444' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#f9f9f9',
                        fontSize: '15px',
                        color: '#333333',
                        transition: 'border-color 0.2s ease',
                      }}
                      className="focus:outline-none focus:border-blue-400"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="organization" style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#444444' }}>
                      Organisation
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#f9f9f9',
                        fontSize: '15px',
                        color: '#333333',
                        transition: 'border-color 0.2s ease',
                      }}
                      className="focus:outline-none focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '8px', color: '#444444' }}>
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#f9f9f9',
                        fontSize: '15px',
                        color: '#333333',
                        transition: 'border-color 0.2s ease',
                        resize: 'none'
                      }}
                      className="focus:outline-none focus:border-blue-400"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      padding: '14px 24px',
                      backgroundColor: '#0071e3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontWeight: 500,
                      fontSize: '16px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1,
                      transition: 'all 0.2s ease',
                    }}
                    className="hover:bg-blue-600 active:bg-blue-700"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>

                  {submitStatus === 'success' && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{ textAlign: 'center', color: '#10b981' }}
                      className="mt-4"
                    >
                      Merci pour votre message ! Nous vous répondrons rapidement.
                    </motion.p>
                  )}

                  {submitStatus === 'error' && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{ textAlign: 'center', color: '#ef4444' }}
                      className="mt-4"
                    >
                      Une erreur s'est produite. Veuillez réessayer.
                    </motion.p>
                  )}
                </div>
              </form>

              {/* Effet de lumière */}
              <div
                style={{
                  position: 'absolute',
                  width: '300px',
                  height: '300px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(191, 90, 242, 0.15) 0%, rgba(255, 255, 255, 0) 70%)',
                  zIndex: -1,
                  opacity: 0.6,
                  top: '-40px',
                  right: '-40px',
                  filter: 'blur(40px)'
                }}
              ></div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModernSwissAvengers;